import PhoneIcon from "@components/icons/PhoneIcon";
import { CONTACT_FORM_INITIAL_VALUE } from "@components/utils/constants";
import Form from "@libs/forms/Form";
import {
  ERROR_TEXTS,
  INITIAL_FORM_VALUE,
  MAX_LENGTH,
} from "@libs/forms/utils/constants";
import FormModal from "@modules/requestAQuote/FormModal";
import { ITag } from "@modules/requestAQuote/components/TripPreferences";
import { FiltersContext, IFiltersContext } from "@providers/FiltersProvider";
import { Dayjs } from "dayjs";
import Link from "next/link";
import { useContext, useMemo, useRef } from "react";
import { object, string } from "yup";
import LayoutBStepOne from "./LayoutBStepOne";
import LayoutBStepTwo from "./LayoutBStepTwo";
import LayoutBSuccessScreen from "./LayoutBSuccessScreen";
import RaQFormButton from "./RaQFormButton";
export const MAX_RAQ_FORM_WIDTH = 600;
const BUTTON_ID: {
  [key: number]: string;
} = {
  1: "continue-btn-raq",
  2: "submit-btn-raq",
  3: "call-btn-raq",
};
interface contentByStepsMap {
  [key: number]: {
    title: string;
    subtitle: string | undefined;
    content: JSX.Element;
    buttonTitle: JSX.Element | string;
  };
}
interface IRaQForm {
  calendarInputProps: any;
  step: number;
  setStep: any;
  isMobile: any;
  tripPreferencesProps: any;
  isOnStickyRaQ?: any;
  closeRaQ?: any;
  phone: any;
  dates: {
    startDate?: Dayjs | null;
    endDate?: Dayjs | null;
  };
}
interface IdefaultValues {
  fullName: string;
  email: string;
  phoneNumber: string;
  datepicker: string;
  importantTags: ITag[];
  accommodations: ITag[];
  activities: ITag[];
  adults: number;
  teens: number;
  children: number;
  rooms: number;
  budgets: { min: number; max: number };
  description: string;
}
const RAQForm = ({
  calendarInputProps,
  step,
  setStep,
  isMobile,
  tripPreferencesProps,
  isOnStickyRaQ,
  closeRaQ,
  phone,
  dates,
}: IRaQForm): JSX.Element => {
  const info = useMemo(() => {
    return CONTACT_FORM_INITIAL_VALUE;
  }, []);

  const raqFormScrollContainerRef = useRef<HTMLDivElement>(null);
  const { syncFilterBar } = useContext(FiltersContext) as IFiltersContext;
  const validationSchema = object().shape({
    fullName: string()
      .required(ERROR_TEXTS.requiredFullName)
      .max(MAX_LENGTH.name, ERROR_TEXTS.maxFullName),
    email: string()
      .required(ERROR_TEXTS.requiredEmail)
      .email(ERROR_TEXTS.validEmail),
  });
  const defaultValues = useMemo(() => {
    return {
      ...info,
      datepicker: calendarInputProps.inputDate
        ? calendarInputProps.inputDate
        : "",
      importantTags: [],
      accommodations: [],
      activities: [],
      adults: INITIAL_FORM_VALUE.adults,
      teens: INITIAL_FORM_VALUE.teens,
      children: INITIAL_FORM_VALUE.children,
      rooms: INITIAL_FORM_VALUE.rooms,
      budgets: {
        min: INITIAL_FORM_VALUE.budgets.min,
        max: INITIAL_FORM_VALUE.budgets.max,
      },
      description: "",
    } as IdefaultValues;
  }, [calendarInputProps, INITIAL_FORM_VALUE, info]);

  const layoutBStepOneProps = {
    isOnStickyRaQ,
    calendarInputProps,
    tripPreferencesProps,
  };

  const contentBySteps: contentByStepsMap = {
    1: {
      title: "Get Local, Expert Advice.",
      subtitle: "Let us design your worry-free trip.",
      content: <LayoutBStepOne {...layoutBStepOneProps} />,
      buttonTitle: "Continue",
    },
    2: {
      title: "Get Local, Expert Advice.",
      subtitle: "",
      content: (
        <LayoutBStepTwo
          scrollContainerRef={isMobile ? raqFormScrollContainerRef : undefined}
        />
      ),
      buttonTitle: "Let's Do This!",
    },
    3: {
      title: !isMobile ? "Success" : "",
      subtitle: undefined,
      content: <LayoutBSuccessScreen />,
      buttonTitle: (
        <Link
          href={phone.phoneAction}
          prefetch={false}
          className="flex flex-row gap-2"
        >
          <div className="m-auto h-[18px] w-[18px]">
            <PhoneIcon phoneStroke={"stroke-white"} />
          </div>
          {phone.phoneNumber}
        </Link>
      ),
    },
  };
  return (
    <>
      {isMobile ? (
        <FormModal
          title={contentBySteps[step].title}
          subtitle={contentBySteps[step].subtitle}
          backModalHandler={
            step === 2
              ? () => {
                  setStep(1);
                  syncFilterBar();
                }
              : undefined
          }
          closeModalHandler={() => {
            closeRaQ();
            syncFilterBar();
          }}
          hasLogo={step === 3}
          maxDesktopWidth={MAX_RAQ_FORM_WIDTH}
          scrollContainerRef={isMobile ? raqFormScrollContainerRef : undefined}
        >
          <Form
            validationSchema={validationSchema}
            defaultValues={defaultValues}
            action="/thank-you"
          >
            <div className="h-full overflow-y-auto desktop:h-fit desktop:px-[6.125rem]">
              {contentBySteps[step].content}
            </div>
            <div className={`z-[100] my-[8px] desktop:mt-8 desktop:mb-5`}>
              <RaQFormButton
                id={BUTTON_ID[step]}
                isMobile={isMobile}
                step={step}
                setStep={setStep}
                tags={{
                  accommodationTags: tripPreferencesProps.accommodationTags,
                  autoPlacesTags: tripPreferencesProps.autoPlacesTags,
                  importantTags: tripPreferencesProps.importantTags,
                  locations: tripPreferencesProps.locations,
                }}
                dates={dates}
              >
                {contentBySteps[step].buttonTitle}
              </RaQFormButton>
            </div>
          </Form>
        </FormModal>
      ) : (
        <Form
          validationSchema={validationSchema}
          defaultValues={defaultValues}
          action="/thank-you"
        >
          {contentBySteps[step].content}
          <div className="static z-[100]">
            <RaQFormButton
              id={BUTTON_ID[1]}
              isMobile={isMobile}
              step={step}
              setStep={setStep}
              tags={{
                accommodationTags: tripPreferencesProps.accommodationTags,
                autoPlacesTags: tripPreferencesProps.autoPlacesTags,
                importantTags: tripPreferencesProps.importantTags,
                locations: tripPreferencesProps.locations,
              }}
              isInStickyContainer={true}
              dates={dates}
            >
              {contentBySteps[step].buttonTitle}
            </RaQFormButton>
          </div>
        </Form>
      )}
    </>
  );
};
export default RAQForm;
